/**
 * Shared hook for handling upload progress tracking via Server-Sent Events
 * Provides common progress state management for both photo and CSV uploads
 */

import { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config';

export interface UploadProgressState {
  uploadStatus: string;
  uploadProgress: number;
  currentFileStatus: string;
  currentFileIndex: number;
  detailedStatus: string;
  currentStage: string;
  processedFiles: any[];
  uploadResponse: any;
  uploadedMenuId: string | null;
}

export interface SSEProgressData {
  type: string;
  message: string;
  progress: number;
  stage?: string;
  detailedStatus?: string;
  currentFile?: number;
  totalFiles?: number;
  fileName?: string;
  itemsExtracted?: number;
  restaurantId?: string;
  menuId?: string;
  restaurantName?: string;
  menuName?: string;
  filesProcessed?: number;
  itemsProcessed?: number;
  details?: any[];
}

/**
 * Custom hook for managing upload progress via Server-Sent Events.
 * Connects to /api/upload/progress/:clientId (legacy upload pipeline).
 * @param clientId - Client ID for SSE connection
 * @param onComplete - Callback when upload completes
 * @param onError - Callback when upload fails
 * @returns Progress state and control functions
 */
export const useUploadProgress = (
  clientId: string | null,
  onComplete?: (data: any) => void,
  onError?: (error: string) => void
) => {
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [currentFileStatus, setCurrentFileStatus] = useState<string>('');
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [detailedStatus, setDetailedStatus] = useState<string>('');
  const [currentStage, setCurrentStage] = useState<string>('');
  const [processedFiles, setProcessedFiles] = useState<any[]>([]);
  const [uploadResponse, setUploadResponse] = useState<any>(null);
  const [uploadedMenuId, setUploadedMenuId] = useState<string | null>(null);
  
  const eventSourceRef = useRef<EventSource | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  // Keep refs up to date without triggering reconnects
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Clean up EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Set up SSE connection when clientId is available
  useEffect(() => {
    if (!clientId) return;

    // Use the same base URL as the API (from shared config)
    // Note: EventSource doesn't support relative URLs, so we need to construct the full URL
    // If API_URL is already a full URL, use it; otherwise construct from window.location
    let baseUrl = API_URL;
    if (baseUrl.startsWith('/')) {
      // Relative URL - construct full URL from current origin
      baseUrl = `${window.location.origin}${baseUrl}`;
    }
    const progressPath = 'upload/progress';
    const sseUrl = `${baseUrl}/${progressPath}/${clientId}`;

    console.log('🔗 [UPLOAD PROGRESS] Creating SSE connection to:', sseUrl);
    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;
    
    eventSource.onmessage = (event) => {
      console.log('📨 [UPLOAD PROGRESS] SSE message received:', event.data);
      try {
        const data: SSEProgressData = JSON.parse(event.data);
        console.log('📨 [UPLOAD PROGRESS] Parsed SSE data:', data);
        
        switch (data.type) {
          case 'connected':
            console.log('✅ [UPLOAD PROGRESS] Progress tracking connected');
            break;
            
          case 'progress':
            console.log('📊 [UPLOAD PROGRESS] Progress update:', data);
            setUploadStatus(data.message);
            setUploadProgress(data.progress);
            setCurrentStage(data.stage || '');
            setDetailedStatus(data.detailedStatus || data.message);
            
            if (data.stage === 'processing_file') {
              setCurrentFileIndex((data.currentFile || 1) - 1);
              setCurrentFileStatus(`Processing: ${data.fileName}`);
            } else if (data.stage === 'file_complete') {
              setCurrentFileStatus(`Completed: ${data.fileName} (${data.itemsExtracted} items)`);
            } else {
              setCurrentFileStatus(data.message);
            }
            break;
            
          case 'complete':
            console.log('🎉 [UPLOAD PROGRESS] Upload complete:', data);
            setUploadStatus('Upload complete!');
            setUploadProgress(100);
            setCurrentFileStatus('Complete');
            setUploadResponse(data);
            setProcessedFiles(data.details || []);
            setUploadedMenuId(data.menuId || null);

            // Close the EventSource
            eventSource.close();
            eventSourceRef.current = null;

            if (onCompleteRef.current) {
              onCompleteRef.current(data);
            }
            break;

          case 'error':
            console.log('❌ [UPLOAD PROGRESS] Upload error:', data);
            setUploadStatus('Upload failed');
            setUploadProgress(0);
            setCurrentFileStatus('Error occurred');

            // Close the EventSource
            eventSource.close();
            eventSourceRef.current = null;

            if (onErrorRef.current) {
              onErrorRef.current(data.message || 'Upload failed');
            }
            break;
        }
      } catch (error) {
        console.error('❌ [UPLOAD PROGRESS] Error parsing SSE data:', error);
      }
    };
    
    eventSource.onerror = (error) => {
      console.error('❌ [UPLOAD PROGRESS] SSE connection error:', error);
      eventSource.close();
      eventSourceRef.current = null;
    };
    
    eventSource.onopen = () => {
      console.log('✅ [UPLOAD PROGRESS] SSE connection opened');
    };

    // Cleanup function
    return () => {
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [clientId]);

  // Reset progress state
  const resetProgress = () => {
    setUploadStatus('');
    setUploadProgress(0);
    setCurrentFileStatus('');
    setCurrentFileIndex(0);
    setDetailedStatus('');
    setCurrentStage('');
    setProcessedFiles([]);
    setUploadResponse(null);
    setUploadedMenuId(null);
  };

  return {
    uploadStatus,
    uploadProgress,
    currentFileStatus,
    currentFileIndex,
    detailedStatus,
    currentStage,
    processedFiles,
    uploadResponse,
    uploadedMenuId,
    resetProgress
  };
};
